import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CohortRuleConditions } from "@unbox/api";
import { handleError } from "@/api/error";
import { invalidateRulesDashboard } from "@/api/rules";
import { client, useScopedMerchantClient } from "./scoped-merchant-client";

// ── Query key builders ────────────────────────────────────────────────────────

const cohortRulesKey = (merchantId: string, workspaceId: string | null | undefined) =>
  ["cohort-rules", merchantId, workspaceId] as const;

const cohortRulesCountKey = (merchantId: string, workspaceId: string | null | undefined) =>
  ["cohort-rules-count", merchantId, workspaceId] as const;

const cohortExemptionsKey = (merchantId: string, workspaceId: string | null | undefined) =>
  ["cohort-exemptions", merchantId, workspaceId] as const;

const cohortRulePreviewKey = (
  merchantId: string,
  workspaceId: string | null | undefined,
  conditions: CohortRuleConditions | null,
) => ["cohort-rule-preview", merchantId, workspaceId, conditions] as const;

// ── Invalidation ───────────────────────────────────────────────────────────────

const invalidateCohortRules = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: ["cohort-rules"] });
  queryClient.invalidateQueries({ queryKey: ["cohort-rules-count"] });
  queryClient.invalidateQueries({ queryKey: ["creator-cohorts"] });
  invalidateRulesDashboard(queryClient);
};

// ── Rules CRUD ─────────────────────────────────────────────────────────────────

export const useCohortRules = () => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: cohortRulesKey(scope.merchantId, scope.workspaceId),
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["creator-cohorts"].rules.$get({
            param: scope.param,
          })
        : await client.merchant[":merchantId"]["creator-cohorts"].rules.$get({ param: scope.param });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    enabled: !!scope.merchantId,
  });
};

export const useCohortRulesCount = () => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: cohortRulesCountKey(scope.merchantId, scope.workspaceId),
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["creator-cohorts"].rules.count.$get({
            param: scope.param,
          })
        : await client.merchant[":merchantId"]["creator-cohorts"].rules.count.$get({ param: scope.param });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    enabled: !!scope.merchantId,
  });
};

export const useCreateCohortRule = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      name,
      cohortId,
      conditions,
    }: {
      name: string;
      cohortId: string;
      conditions: CohortRuleConditions;
    }) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["creator-cohorts"].rules.$post({
            param: scope.param,
            json: { name, cohortId, conditions },
          })
        : await client.merchant[":merchantId"]["creator-cohorts"].rules.$post({
            param: scope.param,
            json: { name, cohortId, conditions },
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    onSuccess: () => invalidateCohortRules(queryClient),
  });
};

export const useUpdateCohortRule = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      ruleId,
      ...patch
    }: {
      ruleId: string;
      name?: string;
      conditions?: CohortRuleConditions;
      isActive?: boolean;
      unassignAlreadyAssigned?: boolean;
    }) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["creator-cohorts"].rules[":ruleId"].$patch({
            param: { ...scope.param, ruleId },
            json: patch,
          })
        : await client.merchant[":merchantId"]["creator-cohorts"].rules[":ruleId"].$patch({
            param: { ...scope.param, ruleId },
            json: patch,
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    onSuccess: () => invalidateCohortRules(queryClient),
  });
};

export const useDeleteCohortRule = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ruleId: string) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["creator-cohorts"].rules[":ruleId"].$delete({
            param: { ...scope.param, ruleId },
          })
        : await client.merchant[":merchantId"]["creator-cohorts"].rules[":ruleId"].$delete({
            param: { ...scope.param, ruleId },
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    onSuccess: () => invalidateCohortRules(queryClient),
  });
};

// ── Impact preview (draft, not persisted) ───────────────────────────────────────

export const useCohortRulePreview = (conditions: CohortRuleConditions | null) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: cohortRulePreviewKey(scope.merchantId, scope.workspaceId, conditions),
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["creator-cohorts"].rules.preview.$post({
            param: scope.param,
            json: conditions as CohortRuleConditions,
          })
        : await client.merchant[":merchantId"]["creator-cohorts"].rules.preview.$post({
            param: scope.param,
            json: conditions as CohortRuleConditions,
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    enabled: !!scope.merchantId && conditions !== null,
  });
};

// ── Exemptions ("individually exempt" creators, exempt from all rules) ─────────

export const useCohortExemptions = () => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: cohortExemptionsKey(scope.merchantId, scope.workspaceId),
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["creator-cohorts"].exemptions.$get({
            param: scope.param,
          })
        : await client.merchant[":merchantId"]["creator-cohorts"].exemptions.$get({ param: scope.param });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    enabled: !!scope.merchantId,
  });
};

export const useAddCohortExemptions = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (creatorIds: string[]) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["creator-cohorts"].exemptions.$post({
            param: scope.param,
            json: { creatorIds },
          })
        : await client.merchant[":merchantId"]["creator-cohorts"].exemptions.$post({
            param: scope.param,
            json: { creatorIds },
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cohort-exemptions"] });
      invalidateCohortRules(queryClient);
    },
  });
};

export const useRemoveCohortExemption = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (creatorId: string) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["creator-cohorts"].exemptions[
            ":creatorId"
          ].$delete({
            param: { ...scope.param, creatorId },
          })
        : await client.merchant[":merchantId"]["creator-cohorts"].exemptions[":creatorId"].$delete({
            param: { ...scope.param, creatorId },
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cohort-exemptions"] });
      invalidateCohortRules(queryClient);
    },
  });
};
